import React, { useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { auth } from '../firebase.ts';
import './AdminLogin.css';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useRedirect, setUseRedirect] = useState(false);

  // Check for redirect result on component mount
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // Client-side domain validation
          if (!result.user.email?.endsWith('@sweetspotfarms.com')) {
            await auth.signOut();
            setError('Access restricted to @sweetspotfarms.com email addresses');
            return;
          }

          onLoginSuccess();
        }
      } catch (err: any) {
        setError('Failed to complete Google sign-in. Please try again.');
      }
    };

    checkRedirectResult();
  }, [onLoginSuccess]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        hd: 'sweetspotfarms.com' // Hint for Google to show sweetspotfarms.com accounts
      });

      if (useRedirect) {
        await signInWithRedirect(auth, provider);
        // The page will redirect, so we don't continue execution here
        return;
      }

      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError: any) {
        // If popup fails due to CORS/Cross-Origin-Opener-Policy, try redirect
        if (popupError.message?.includes('Cross-Origin-Opener-Policy') ||
            popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/cancelled-popup-request') {
          setUseRedirect(true);
          await signInWithRedirect(auth, provider);
          return;
        }

        throw popupError; // Re-throw other errors
      }

      // Client-side domain validation
      if (!result.user.email?.endsWith('@sweetspotfarms.com')) {
        await auth.signOut();
        setError('Access restricted to @sweetspotfarms.com email addresses');
        setLoading(false);
        return;
      }

      onLoginSuccess();
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked by browser. Please allow pop-ups and try again.');
      } else {
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <h1>Admin Portal</h1>
        <p>Sign in with your Sweetspot Google account</p>
        
        <button 
          className="google-signin-button" 
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading ? 'Signing in...' : useRedirect ? 'Sign in with Google (Redirect)' : 'Sign in with Google (Popup)'}
        </button>
        
        
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default AdminLogin;